import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { cn } from '@rocco/utils/cn';
import { FlexibleModuleEnum, TextTypeEnum } from 'generated/graphql';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import { RevampRichText } from './RevampRichText';
import { RevampImageFigure } from './RevampImageFigure';
import { RevampQuoteModule } from './RevampQuoteModule';
import { RevampSloganModule } from './RevampSloganModule';
import { RevampGalleryModule } from './RevampGalleryModule';
import { RevampCollectionModule } from './RevampCollectionModule';
import { RevampProfessionalCtas } from './RevampProfessionalCtas';
import { RevampVideoModule } from './RevampVideoModule';
import { RevampModuleSection } from './RevampModuleSection';
import { DetailScrollReveal } from '~/ui-rocco/pages/detail-shared';
import {
    REVAMP_IMAGE_COLUMN_SIZES,
    REVAMP_IMAGE_SPREAD_SIZES,
    REVAMP_IMAGE_WIDE_SIZES,
} from '../../util/revamp-image-sizes';

/** How many body modules remain visible when article auth gating is on. */
const GATED_MODULE_PREVIEW_COUNT = 2;

type RevampProfessional = { title: string; link: string };

interface RevampModulesProps {
    modules: PageBuilderModuleType[];
    professional?: RevampProfessional;
    /** Truncate and blur body modules for unauthenticated gated readers. */
    articleGatingEnabled?: boolean;
    /**
     * Slotted in below the first module that actually renders — the first
     * treatment the reader sees, skipping any module with no revamp renderer.
     */
    afterFirstModule?: ReactNode;
}

/** Landscape images go full width with side insets; portraits stay a column. */
const isLandscape = (module: PageBuilderModuleType) => {
    const { width, height } = module.columns[0]?.dimension ?? {};
    return Boolean(width && height && width >= height);
};

const renderModule = (
    module: PageBuilderModuleType,
    professional?: RevampProfessional,
    imageNumber?: number,
) => {
    const columns = module.columns;
    const firstColumn = columns[0];
    if (!firstColumn) return null;

    switch (module.presetType) {
        case FlexibleModuleEnum.HeadingModule:
        case FlexibleModuleEnum.SingleParagraphModule:
            // Set on the teaser's measure, so the article's opening paragraph
            // and its body copy run to the same edge down the page rather than
            // stepping in at the first module.
            return (
                <RevampRichText
                    contentHTML={firstColumn.contentHTML}
                    className="w-full px-4.5 md:w-[56.125rem] md:px-0"
                />
            );
        case FlexibleModuleEnum.DuoParagraphModule:
            return (
                <div className="flex w-full flex-col gap-6 px-4.5 md:w-[56.25rem] md:flex-row md:px-0">
                    {columns.slice(0, 2).map((column) => (
                        <RevampRichText
                            key={column.id}
                            contentHTML={column.contentHTML}
                            className="min-w-0 flex-1"
                        />
                    ))}
                </div>
            );
        // Optional title (the module's h3 column) over the LEFT pair of four
        // equal text columns; the right pair top-aligns with the title (Figma
        // "Page Builder / Paragraph" Heading=yes/no side by side, BC-4764).
        // The title's 35px gap to its columns comes from RevampRichText's
        // built-in h3 bottom margin. Empty columns are dropped so the
        // remaining ones keep equal widths.
        case FlexibleModuleEnum.QuadParagraphModule: {
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            const paragraphColumns = columns
                .filter((column) => column.textType !== TextTypeEnum.H3)
                .slice(0, 4)
                .filter((column) => column.contentHTML);
            const leftPair = paragraphColumns.slice(0, 2);
            const rightPair = paragraphColumns.slice(2, 4);
            return (
                <div className="flex w-full flex-col gap-6 px-4.5 md:flex-row md:px-[3rem]">
                    <div className="flex min-w-0 flex-1 flex-col">
                        {titleColumn && (
                            <RevampRichText
                                contentHTML={titleColumn.contentHTML}
                                promoteHeading
                                className="w-full"
                            />
                        )}
                        <div className="flex w-full flex-col gap-6 md:flex-row">
                            {leftPair.map((column) => (
                                <RevampRichText
                                    key={column.id}
                                    contentHTML={column.contentHTML}
                                    className="min-w-0 flex-1"
                                />
                            ))}
                        </div>
                    </div>
                    {rightPair.length > 0 && (
                        <div className="flex min-w-0 flex-1 flex-col gap-6 md:flex-row">
                            {rightPair.map((column) => (
                                <RevampRichText
                                    key={column.id}
                                    contentHTML={column.contentHTML}
                                    className="min-w-0 flex-1"
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        case FlexibleModuleEnum.QuoteModule:
            return (
                <RevampQuoteModule
                    column={firstColumn}
                    professional={professional}
                />
            );
        case FlexibleModuleEnum.SloganModule:
            return (
                <RevampSloganModule
                    column={firstColumn}
                    professional={professional}
                />
            );
        case FlexibleModuleEnum.FullWidthSloganModule:
            return (
                <RevampSloganModule
                    column={firstColumn}
                    professional={professional}
                    variant="fullWidth"
                />
            );
        // Slogan half (hairline, slogan, attribution, CTA pills) beside a
        // text half: optional h3 title over the body copy. Both text columns
        // are paragraphs, so sort order tells the slogan from the body.
        case FlexibleModuleEnum.SloganParagraphModule: {
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            const [sloganColumn, bodyColumn] = columns.filter(
                (column) => column.textType !== TextTypeEnum.H3,
            );
            if (!sloganColumn?.contentHTML && !bodyColumn?.contentHTML) {
                return null;
            }
            // The text half starts at the page centre line (50/50 split),
            // not at the right margin; the copy itself is capped at 35rem.
            return (
                <div className="flex w-full flex-col gap-14 px-4.5 md:flex-row md:gap-0 md:pr-[8.0625rem] md:pl-12">
                    {sloganColumn && (
                        <div className="min-w-0 md:w-[58rem] md:shrink-0 md:pr-6">
                            <div className="md:max-w-[25rem]">
                                <RevampSloganModule
                                    column={sloganColumn}
                                    professional={professional}
                                    variant="half"
                                />
                            </div>
                        </div>
                    )}
                    <div className="min-w-0 md:ml-auto md:w-1/2">
                        <div className="md:w-[45.625rem]">
                            {titleColumn && (
                                <RevampRichText
                                    contentHTML={titleColumn.contentHTML}
                                    promoteHeading
                                    className="[&_h2]:text-[1.125rem] [&_h2]:leading-[1.3] md:[&_h2]:mb-9 md:[&_h2]:text-[1.375rem]"
                                />
                            )}
                            {/* Body copy keeps the standard 1.375rem
                                RevampRichText scale, matching the other
                                paragraph modules. */}
                            {bodyColumn && (
                                <RevampRichText
                                    contentHTML={bodyColumn.contentHTML}
                                />
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        case FlexibleModuleEnum.SingleImageModule:
            return isLandscape(module) ? (
                <RevampImageFigure
                    column={firstColumn}
                    number={imageNumber}
                    sizes={REVAMP_IMAGE_WIDE_SIZES}
                    className="md:px-[12.5rem]"
                />
            ) : (
                <RevampImageFigure
                    column={firstColumn}
                    number={imageNumber}
                    sizes={REVAMP_IMAGE_COLUMN_SIZES}
                    className="px-4.5 md:w-[56.25rem] md:px-0"
                />
            );
        case FlexibleModuleEnum.DuoImageModule:
            return (
                <div className="flex w-full flex-col gap-10 px-4.5 md:flex-row md:gap-6 md:px-[3rem]">
                    {columns.slice(0, 2).map((column) => (
                        <RevampImageFigure
                            key={column.id}
                            column={column}
                            sizes={REVAMP_IMAGE_COLUMN_SIZES}
                            className="min-w-0 flex-1"
                        />
                    ))}
                </div>
            );
        case FlexibleModuleEnum.ImageParagraphModule: {
            const imageColumn = columns.find((column) => column.imageUrl);
            const titleColumn = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            );
            // One body column renders on a 35rem measure; two render side by
            // side filling the half. Empty columns are dropped.
            const bodyColumns = columns.filter(
                (column) =>
                    column.textType !== TextTypeEnum.H3 && column.contentHTML,
            );
            const showCtaButtons = columns.some(
                (column) => column.showCtaButtons,
            );
            if (!imageColumn && !titleColumn && bodyColumns.length === 0) {
                return null;
            }
            // Editorial spread on the same rails as the slogan + paragraph
            // module: small image in the left half with the numbered caption
            // and optional professional CTA pills below, heading + body copy
            // right. The image sits at the outer edge of its half, or toward
            // the centre when the column's AlignRight flag is set.
            // The text half starts at the page centre line (50/50 split),
            // not at the right margin; the copy itself is capped at 35rem.
            return (
                <div className="flex w-full flex-col gap-7 px-4.5 md:flex-row md:gap-0 md:px-12">
                    {imageColumn && (
                        <div
                            className={cn(
                                'md:flex md:w-[58rem] md:shrink-0 md:pr-6',
                                imageColumn.alignRight && 'md:justify-end',
                            )}
                        >
                            {/* With two body columns the image reads as one
                                column of the spread, so it matches their
                                width; the single-body layout keeps the 438px
                                portrait-small image. */}
                            <div
                                className={cn(
                                    'flex min-w-0 flex-col gap-4',
                                    bodyColumns.length > 1
                                        ? 'md:w-[calc(50%-0.75rem)]'
                                        : 'md:w-[27.375rem]',
                                )}
                            >
                                <RevampImageFigure
                                    column={imageColumn}
                                    number={imageNumber}
                                    sizes={REVAMP_IMAGE_SPREAD_SIZES}
                                    captionClassName="md:w-full"
                                />
                                {showCtaButtons && professional && (
                                    <RevampProfessionalCtas
                                        professional={professional}
                                        placement="imageParagraph"
                                        compact
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {(titleColumn || bodyColumns.length > 0) && (
                        <div className="min-w-0 md:ml-auto md:w-1/2">
                            <div
                                className={cn(
                                    bodyColumns.length < 2 &&
                                        'md:w-[45.625rem]',
                                )}
                            >
                                {titleColumn && (
                                    <RevampRichText
                                        contentHTML={titleColumn.contentHTML}
                                        promoteHeading
                                        className="[&_h2]:text-[1.125rem] [&_h2]:leading-[1.3] md:[&_h2]:mb-9 md:[&_h2]:text-[1.375rem]"
                                    />
                                )}
                                {/* Body copy keeps the standard 1.375rem
                                    RevampRichText scale in both the single- and
                                    two-column layouts. */}
                                <div className="flex flex-col gap-6 md:flex-row">
                                    {bodyColumns.map((column) => (
                                        <RevampRichText
                                            key={column.id}
                                            contentHTML={column.contentHTML}
                                            className="min-w-0 flex-1 md:w-[45.625rem]"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }
        case FlexibleModuleEnum.TrioImageModule:
        case FlexibleModuleEnum.QuadImageModule:
        case FlexibleModuleEnum.OctaImageModule: {
            // Optional ruled heading (the module's h3 column). Only the
            // article-only "…with title" presets carry one; the shared gallery
            // presets have images alone and keep their bare grid.
            const titleHTML = columns.find(
                (column) =>
                    column.textType === TextTypeEnum.H3 && column.contentHTML,
            )?.contentHTML;
            const tileColumns = columns.filter(
                (column) => column.textType !== TextTypeEnum.H3,
            );
            // Collection presets carry products; plain galleries are images.
            if (tileColumns.some((column) => column.productId)) {
                return (
                    <RevampCollectionModule
                        columns={tileColumns}
                        titleHTML={titleHTML}
                    />
                );
            }
            const perRow =
                module.presetType === FlexibleModuleEnum.TrioImageModule
                    ? 3
                    : 4;
            return (
                <RevampGalleryModule
                    columns={tileColumns}
                    perRow={perRow}
                    titleHTML={titleHTML}
                />
            );
        }
        case FlexibleModuleEnum.SingleVideoModule:
            return (
                <RevampVideoModule
                    column={firstColumn}
                    className="px-4.5 md:w-[56.25rem] md:px-0"
                />
            );
        // HTML and invoice modules keep no revamp treatment yet — follow-up
        // under BC-4720 once the design covers them.
        default:
            return null;
    }
};

/**
 * Renders the article's page-builder modules with the revamp's editorial
 * treatments. Same CMS data as the legacy DetailModules — only the renderers
 * differ. Self-hides when no module has a revamp treatment. When auth gating
 * is on, only the first two modules render (blurred + faded), matching legacy
 * DetailModules so the sign-in banner can gate the rest of the article.
 */
export const RevampModules = ({
    modules,
    professional,
    articleGatingEnabled = false,
    afterFirstModule,
}: RevampModulesProps) => {
    const modulesToRender = articleGatingEnabled
        ? modules.slice(0, GATED_MODULE_PREVIEW_COUNT)
        : modules;

    // Captioned single images carry a sequential "( N )" figure number
    // (Figma "Description"). Only captioned images consume a number so the
    // visible numbering stays contiguous.
    let imageNumber = 0;
    const rendered = modulesToRender
        .map((module) => {
            const isCaptionedImage =
                (module.presetType === FlexibleModuleEnum.SingleImageModule ||
                    module.presetType ===
                        FlexibleModuleEnum.ImageParagraphModule) &&
                Boolean(
                    module.columns.find((column) => column.imageUrl)?.caption,
                );
            const number = isCaptionedImage ? ++imageNumber : undefined;
            return {
                id: module.id,
                // Read progress against the full module list (legacy
                // DetailModules parity), even when gating truncates the render.
                percentContentViewed:
                    (module.memberSortOrder + 1) / modules.length,
                node: renderModule(module, professional, number),
            };
        })
        .filter((entry) => entry.node !== null);

    if (rendered.length === 0) return null;

    return (
        <div
            className={cn(
                'relative flex w-full flex-col items-center gap-16 md:gap-[11.25rem]',
                articleGatingEnabled &&
                    'max-h-35 overflow-hidden blur-[1px] md:max-h-48',
            )}
        >
            {rendered.map((entry, index) => (
                // Gated previews render immediately — the teaser is clipped and
                // blurred, so a scroll reveal would leave it sitting invisible.
                <Fragment key={entry.id}>
                    <DetailScrollReveal immediate={articleGatingEnabled}>
                        <RevampModuleSection
                            moduleId={entry.id}
                            percentContentViewed={entry.percentContentViewed}
                            className="flex w-full justify-center"
                        >
                            {entry.node}
                        </RevampModuleSection>
                    </DetailScrollReveal>
                    {index === 0 && afterFirstModule && (
                        <DetailScrollReveal immediate={articleGatingEnabled}>
                            {afterFirstModule}
                        </DetailScrollReveal>
                    )}
                </Fragment>
            ))}
            {articleGatingEnabled && (
                <div className="absolute inset-0 bg-linear-to-b from-gray-50/50 to-gray-50" />
            )}
        </div>
    );
};
